import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { nanoid } from "nanoid";
import { Form, Input, Button, Drawer, Tag, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import Disclaimer from "Shared/Disclaimer";
import { Tokens } from "Shared/Tokens";
import * as portfolioAction from "store/Actions/portfolio";
import { mpAddPortfolio } from "Shared/Mixpanel/Portfolios";

const { Title, Text } = Typography;

const SUGGESTED_NAMES = ["Long-term hold", "Core crypto", "Watchlist"];

const AddPortfolio = (props) => {
    const { showAddPortfolioDrawer, onClose } = props;
    const dispatch = useDispatch();
    const userInfo = useSelector((state) => state.users?.userInfo);
    const [form] = Form.useForm();
    const [disabled, setDisabled] = useState(true);
    const [portfolio, setPortfolio] = useState("");
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 768 : false
    );

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleValueChange = (value) => {
        const nextName = String(value.portfolioName || "");
        setPortfolio(nextName);
        setDisabled(!nextName.trim());
    };

    const handleSelectSuggestion = (name) => {
        form.setFieldsValue({ portfolioName: name });
        setPortfolio(name);
        setDisabled(false);
    };

    const onFinish = (values) => {
        const cleanName = String(values.portfolioName || "").trim();
        if (!cleanName) return;
        setPortfolio("");
        form.resetFields();
        onClose(false);
        mpAddPortfolio({
            name: cleanName,
        });
        dispatch(
            portfolioAction.add({
                name: cleanName,
                avatar: "",
                uid: nanoid(),
                createdAt: Date.now(),
            })
        );
    };

    const onFinishFailed = () => {};

    return (
        <div>
            <Drawer
                title={
                    <div
                        style={{
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                        }}
                    >
                        <Title
                            level={4}
                            style={{
                                margin: 0,
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                fontSize: "18px",
                            }}
                        >
                            Create portfolio
                        </Title>
                    </div>
                }
                width={isMobile ? "100%" : 460}
                height={isMobile ? "72%" : "100%"}
                placement={isMobile ? "bottom" : "right"}
                push={false}
                closable
                closeIcon={
                    <CloseOutlined style={{ color: Tokens.textSecondary }} />
                }
                onClose={() => onClose()}
                open={showAddPortfolioDrawer}
                maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
                headerStyle={{
                    backgroundColor: Tokens.bg,
                    borderBottom: `1px solid ${Tokens.hairline}`,
                }}
                bodyStyle={{
                    backgroundColor: Tokens.bg,
                    padding: "24px 16px",
                }}
            >
                <div
                    style={{
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        width: "100%",
                        backgroundColor: Tokens.card,
                        border: `1px solid ${Tokens.hairline}`,
                        borderRadius: Tokens.radiusCard,
                        padding: "20px",
                    }}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={{ portfolioName: portfolio }}
                        onValuesChange={handleValueChange}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        name="add-portfolio"
                        autoComplete="off"
                    >
                        <Form.Item
                            label={
                                <Text style={{ color: Tokens.textSecondary }}>
                                    Portfolio name
                                </Text>
                            }
                            name="portfolioName"
                        >
                            <Input
                                type="text"
                                placeholder="Long-term holdings"
                                style={{
                                    backgroundColor: Tokens.raised,
                                    borderColor: Tokens.hairline,
                                    color: Tokens.textPrimary,
                                    borderRadius: Tokens.radiusControl,
                                    fontVariantNumeric: "tabular-nums",
                                    height: "40px",
                                }}
                            />
                        </Form.Item>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                flexWrap: "wrap",
                                marginTop: "-8px",
                                marginBottom: "16px",
                            }}
                        >
                            <span
                                style={{
                                    color: Tokens.textMuted,
                                    fontSize: "11px",
                                }}
                            >
                                Templates:
                            </span>
                            {SUGGESTED_NAMES.map((suggestion) => (
                                <Tag
                                    key={suggestion}
                                    onClick={() =>
                                        handleSelectSuggestion(suggestion)
                                    }
                                    style={{
                                        margin: 0,
                                        cursor: "pointer",
                                        backgroundColor:
                                            portfolio === suggestion
                                                ? Tokens.accentSubtle
                                                : Tokens.raised,
                                        color:
                                            portfolio === suggestion
                                                ? Tokens.accent
                                                : Tokens.textSecondary,
                                        border: `1px solid ${
                                            portfolio === suggestion
                                                ? Tokens.accentBorder
                                                : Tokens.hairline
                                        }`,
                                        borderRadius: Tokens.radiusTag,
                                        fontSize: "11px",
                                        padding: "2px 8px",
                                    }}
                                >
                                    {suggestion}
                                </Tag>
                            ))}
                        </div>
                        <Form.Item style={{ marginBottom: "12px" }}>
                            <Button
                                disabled={disabled}
                                type="primary"
                                htmlType="submit"
                                block
                                style={{
                                    borderRadius: Tokens.radiusControl,
                                    fontWeight: 500,
                                    height: "40px",
                                }}
                            >
                                {portfolio
                                    ? `Create ${portfolio}`
                                    : "Create portfolio"}
                            </Button>
                        </Form.Item>
                        {!userInfo && <Disclaimer />}
                    </Form>
                </div>
            </Drawer>
        </div>
    );
};

export default AddPortfolio;
